/**
 * Per-step sponsor share: action labels, share/final action ids, schema metadata.
 */

export interface StepShareMeta {
  stepNumber: number;
  stepTitle: string;
  shareName: string;
  documentType: string;
  /** Primary action id with share button */
  shareActionId: number;
  /** Additional action ids that also get a share button (same dialog) */
  secondaryShareActionIds?: number[];
  /** Final "Take Step N" action — excluded from canShare completed-work check */
  finalActionId: number;
  /** Optional custom label for share button (default: Share with sponsor) */
  shareButtonText?: string;
  /** Ordered action labels for summary HTML (id -> short label) */
  actionLabels: Record<number, string>;
  /** Total actions after any schema v2 insertions */
  actionCount: number;
}

const SHARE_ACTION_TITLE = 'Share and review your work with your sponsor';

export const STEP_SHARE_META: Record<number, StepShareMeta> = {
  1: {
    stepNumber: 1,
    stepTitle: 'Step One',
    shareName: 'Step One Summary',
    documentType: 'step-1',
    shareActionId: 4,
    finalActionId: 6,
    shareButtonText: 'Share progress with sponsor',
    actionCount: 6,
    actionLabels: {
      1: 'Read Green Book: Step One',
      2: 'How to work Step One (guidance & video)',
      3: 'Prepare my First Step story',
      4: 'Talk with my sponsor about my First Step',
      5: 'Read my First Step in a meeting',
      6: 'Take Step One',
    },
  },
  2: {
    stepNumber: 2,
    stepTitle: 'Step Two',
    shareName: 'Step Two Summary',
    documentType: 'step-2',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Two',
      2: 'How to work Step Two (guidance & video)',
      3: 'Answer Step Two questions',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Two: Declare willingness',
    },
  },
  3: {
    stepNumber: 3,
    stepTitle: 'Step Three',
    shareName: 'Step Three Summary',
    documentType: 'step-3',
    shareActionId: 5,
    finalActionId: 6,
    actionCount: 6,
    actionLabels: {
      1: 'Read Green Book: Step Three',
      2: 'How to work Step Three (guidance & video)',
      3: 'Answer Step Three questions',
      4: 'Third Step Prayer: read and write my own if I choose',
      5: SHARE_ACTION_TITLE,
      6: 'Take Step Three',
    },
  },
  4: {
    stepNumber: 4,
    stepTitle: 'Step Four',
    shareName: 'Step Four Summary',
    documentType: 'step-4',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Understand Step Four',
      2: 'How to work Step Four (guidance & video)',
      3: 'Fill out inventories and schedule Step Five',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Four',
    },
  },
  5: {
    stepNumber: 5,
    stepTitle: 'Step Five',
    shareName: 'Step Five Summary',
    documentType: 'step-5',
    shareActionId: 5,
    finalActionId: 6,
    actionCount: 6,
    actionLabels: {
      1: 'Read Green Book: Step Five',
      2: 'How to work Step Five (guidance & video)',
      3: 'Present Step Four inventories to my sponsor',
      4: 'Identify my character defects',
      5: 'Share and review my character defects with my sponsor',
      6: 'Take Step Five',
    },
  },
  6: {
    stepNumber: 6,
    stepTitle: 'Step Six',
    shareName: 'Step Six Summary',
    documentType: 'step-6',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Six',
      2: 'How to work Step Six (guidance & video)',
      3: 'Work through character defects',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Six',
    },
  },
  7: {
    stepNumber: 7,
    stepTitle: 'Step Seven',
    shareName: 'Step Seven Summary',
    documentType: 'step-7',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Seven',
      2: 'How to work Step Seven (guidance & video)',
      3: 'Read the Seventh Step Prayer — and write my own if I choose',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Seven: read the Seventh Step Prayer aloud',
    },
  },
  8: {
    stepNumber: 8,
    stepTitle: 'Step Eight',
    shareName: 'Step Eight Summary',
    documentType: 'step-8',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Eight',
      2: 'How to work Step Eight (guidance & video)',
      3: 'Make a list of persons I have harmed',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Eight',
    },
  },
  9: {
    stepNumber: 9,
    stepTitle: 'Step Nine',
    shareName: 'Step Nine Summary',
    documentType: 'step-9',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Nine',
      2: 'How to work Step Nine (guidance & video)',
      3: 'Make direct amends',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Nine',
    },
  },
  10: {
    stepNumber: 10,
    stepTitle: 'Step Ten',
    shareName: 'Step Ten Summary',
    documentType: 'step-10',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Ten',
      2: 'Read guidance on working Step Ten',
      3: 'Practice personal inventory',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Ten',
    },
  },
  11: {
    stepNumber: 11,
    stepTitle: 'Step Eleven',
    shareName: 'Step Eleven Summary',
    documentType: 'step-11',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Eleven',
      2: 'Read guidance on working Step Eleven',
      3: 'Define my daily quiet time plan and practice it',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Eleven',
    },
  },
  12: {
    stepNumber: 12,
    stepTitle: 'Step Twelve',
    shareName: 'Step Twelve Summary',
    documentType: 'step-12',
    shareActionId: 4,
    finalActionId: 5,
    actionCount: 5,
    actionLabels: {
      1: 'Read Green Book: Step Twelve',
      2: 'Read guidance on working Step Twelve',
      3: 'Define my service plan',
      4: SHARE_ACTION_TITLE,
      5: 'Take Step Twelve',
    },
  },
};

export function getStepShareMeta(stepNumber: number): StepShareMeta {
  const meta = STEP_SHARE_META[stepNumber];
  if (!meta) {
    throw new Error(`No share config for step ${stepNumber}`);
  }
  return meta;
}

export const SHARE_BUTTON_TEXT = 'Share with sponsor';

export const DEFAULT_SHARE_EMPTY_MESSAGE =
  'Complete at least one action or add some work on this step before sharing.';
